import React, { useMemo, useState } from 'react';
import { useProducts } from '../useProducts';
import { formatProductTitle } from '../formatProductTitle';

const MAX = 8;

function FeaturedPickRow({ pick, product, index, picksLength, onMove, onRemove }) {
  return (
    <li className="list-group-item d-flex flex-wrap align-items-center gap-2 py-2">
      {product.image ? (
        <img
          src={product.image}
          alt=""
          width={40}
          height={40}
          className="rounded flex-shrink-0"
          style={{ width: 40, height: 40, objectFit: 'cover' }}
        />
      ) : null}
      <div className="flex-grow-1 min-width-0">
        <PickTitle product={product} />
        <div className="small text-muted">
          {pick.category_slug} · {pick.product_id}
        </div>
      </div>
      <div className="btn-group btn-group-sm">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => onMove(index, -1)}
          disabled={index === 0}
          aria-label="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => onMove(index, 1)}
          disabled={index === picksLength - 1}
          aria-label="Move down"
        >
          ↓
        </button>
        <button type="button" className="btn btn-outline-danger" onClick={() => onRemove(index)} aria-label="Remove">
          ×
        </button>
      </div>
    </li>
  );
}

function PickTitle({ product }) {
  return <div className="fw-semibold text-truncate">{formatProductTitle(product.name)}</div>;
}

export default function AdminSiteFeaturedPicker({ featured, onChange }) {
  const { allProducts, loading } = useProducts();
  const [q, setQ] = useState('');

  const picks = Array.isArray(featured) ? featured : [];

  const productMap = useMemo(() => {
    const m = new Map();
    for (const p of allProducts) {
      if (!p?.category || p.id == null) continue;
      m.set(`${p.category}:${String(p.id)}`, p);
    }
    return m;
  }, [allProducts]);

  const resolved = useMemo(
    () =>
      picks
        .map((pick) => {
          const key = `${pick.category_slug}:${pick.product_id}`;
          const p = productMap.get(key);
          return p ? { pick, product: p, key } : null;
        })
        .filter(Boolean),
    [picks, productMap],
  );

  const pickKeys = useMemo(
    () => new Set(picks.map((p) => `${p.category_slug}:${p.product_id}`)),
    [picks],
  );

  const searchResults = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term || picks.length >= MAX) return [];
    return allProducts
      .filter((p) => {
        const key = `${p.category}:${String(p.id)}`;
        if (pickKeys.has(key)) return false;
        const name = String(p.name || '').toLowerCase();
        const id = String(p.id || '').toLowerCase();
        const cat = String(p.category || '').toLowerCase();
        return name.includes(term) || id.includes(term) || cat.includes(term);
      })
      .slice(0, 12);
  }, [allProducts, q, pickKeys, picks.length]);

  const addProduct = (p) => {
    if (picks.length >= MAX) return;
    const category_slug = String(p.category || '').trim();
    const product_id = String(p.id ?? '').trim();
    if (!category_slug || !product_id) return;
    if (pickKeys.has(`${category_slug}:${product_id}`)) return;
    onChange([...picks, { category_slug, product_id }]);
    setQ('');
  };

  const removeAt = (index) => {
    onChange(picks.filter((_, i) => i !== index));
  };

  const move = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= picks.length) return;
    const copy = [...picks];
    const [item] = copy.splice(index, 1);
    copy.splice(next, 0, item);
    onChange(copy);
  };

  return (
    <div className="card border shadow-sm mb-3">
      <div className="card-header py-2">
        <h2 className="h6 mb-0">Home featured products</h2>
      </div>
      <div className="card-body">
        <p className="small text-muted mb-3">
          Pick up to {MAX} products for the home page. <strong>Order matters:</strong> #1–#3 appear as
          &quot;Editor&apos;s picks&quot; on the hero; all picks show in the Featured products carousel below.
          Product #1 is also the hero photo fallback when no hero photo is set in Content. Click{' '}
          <strong>Save</strong> when done.
        </p>

        {loading ? (
          <p className="small text-muted mb-0">Loading catalog…</p>
        ) : (
          <>
            <label className="form-label small" htmlFor="featured-search">
              Add product
            </label>
            <input
              id="featured-search"
              type="search"
              className="form-control form-control-sm mb-2"
              placeholder="Search by name, ID, or category…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              disabled={picks.length >= MAX}
            />
            {searchResults.length > 0 && (
              <ul className="list-group list-group-flush border rounded mb-3 admin-featured-search">
                {searchResults.map((p) => (
                  <li key={`${p.category}-${p.id}`} className="list-group-item py-2 px-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-start p-0 w-100 text-decoration-none"
                      onClick={() => addProduct(p)}
                    >
                      <span className="fw-semibold">{formatProductTitle(p.name)}</span>
                      <span className="d-block small text-muted">
                        {p.category} · ID {p.id}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {resolved.length === 0 ? (
              <p className="small text-muted mb-0">No featured products selected.</p>
            ) : (
              <ol className="list-group list-group-numbered admin-featured-picks mb-0">
                {resolved.map(({ pick, product, key }, index) => (
                  <FeaturedPickRow
                    key={key}
                    pick={pick}
                    product={product}
                    index={index}
                    picksLength={picks.length}
                    onMove={move}
                    onRemove={removeAt}
                  />
                ))}
              </ol>
            )}
          </>
        )}
      </div>
    </div>
  );
}
